import { useRuntimeVariables } from 'app-kit'
import { AxiosError, AxiosResponse } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { useMemo } from 'react'

/**
 * The request payload for fetching a query response from Gecko Sage.
 */
export interface FetchSageQueryRequest {
  query: string
  context?: string
  organizationId?: string
  tags?: Record<string, string>
}

/**
 * Represents a Rolodex file reference for the v2 API.
 */
export interface RolodexFile {
  id: string
  path: string
  mime_type?: string
}

export interface GeminiInlineFile {
  data_base64: string
  mime_type: string
  filename?: string
}

/**
 * The request payload for fetching a query response from Vertex API.
 */
export interface FetchVertexQueryRequest {
  query: string
  rolodexFiles?: ReadonlyArray<RolodexFile>
  inlineFiles?: ReadonlyArray<GeminiInlineFile>
  systemPrompt?: string
  responseSchema?: Record<string, unknown>
  modelName?: string
}

/**
 * The request payload for V2 LLM service with Pinecone RAG.
 */
export interface FetchV2LLMRequest {
  content: string
  rolodex_files?: Array<{
    id: string
    path: string
  }>
}

/**
 * The request payload for RAG Pinecone endpoint.
 */
export interface FetchRAGPineconeRequest {
  content: string
  organization_id: string
  additional_context?: string
  top_k?: number
  filters?: {
    [key: string]: string
  }
  rolodex_files?: Array<{
    id: string
    path: string
    mime_type?: string
  }>
  response_schema?: Record<string, unknown>
  include_rug_data?: boolean
}

/**
 * The request payload for fast uploading a file to Pinecone for RAG purposes.
 */
export interface FastUploadToPineconeRequest {
  rolodex_file: RolodexFile & { mime_type?: string }
  organization_id: string
  tags?: {
    [key: string]: string
  }
  add_metadata: boolean
}

/**
 * The request payload for checking if a file exists in Pinecone by tag.
 */
export interface CheckFileIdRequest {
  tag_name: string
  tag_value: string
  organization_id: string
}

export interface Citation {
  organization_id: string
  page_number: number
  report: string
}

/**
 * The response payload received from Gecko Sage after requesting a query.
 */
export interface FetchSageQueryResponse {
  result: string
  context: Array<Citation>
}

/**
 * The response payload received from Pinecone fast upload endpoint.
 */
export interface FastUploadToPineconeResponse {
  message?: string
}

/**
 * Represents an error object for Sage API interactions.
 */
export interface SageApiError {
  message: string
  status?: number
  details?: unknown
}

type SageErrorResponsePayload = {
  error?: string
  detail?: unknown
  details?: unknown
}

export function isSageApiErrorWithCode(
  error: unknown,
  message: string,
  statusCode: number | 404
): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  const sageError = error as Partial<SageApiError>
  return (
    sageError.status === statusCode &&
    typeof sageError.details === 'string' &&
    sageError.details.includes(String(message))
  )
}

/**
 * Custom hook to interact with the Gecko Sage microservice for fetching AI-driven query responses.
 * @returns An object containing functions to interact with the Sage API.
 */
export function useSageService() {
  const { LLM_SERVICE } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  return useMemo(() => {
    const fetchVertexQuery = async (
      requestPayload: FetchVertexQueryRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL with query parameter
      const url = `${LLM_SERVICE}/api/v1/gemini/generate`
      try {
        const payload = {
          content: requestPayload.query,
          rolodex_files: requestPayload.rolodexFiles || [],
          inline_files: requestPayload.inlineFiles || [],
          system_prompt: requestPayload.systemPrompt,
          response_schema: requestPayload.responseSchema,
          model_name: requestPayload.modelName,
        }

        const response: AxiosResponse<FetchSageQueryResponse> =
          await axios.post(url, payload)
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const responseData = axiosError.response?.data as
          | SageErrorResponsePayload
          | undefined
        const errorMessage =
          responseData?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching Vertex query response.'
        const status = axiosError.response?.status
        const errorDetails = responseData?.details

        console.error('Error fetching Vertex query response:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    const uploadFileToSage = async ({
      file,
      organization,
      metadata,
    }: {
      file: File
      organization: string
      metadata: Record<string, string>
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('organization_id', organization)
      formData.append('tags', JSON.stringify(metadata))
      try {
        const response: AxiosResponse<{ success: boolean; message: string }> =
          await axios.post(
            `${LLM_SERVICE}/api/v2/rag/pinecone/upload`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          )
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const errorMessage =
          (axiosError.response?.data as { error?: string })?.error ||
          axiosError.message ||
          'An unknown error occurred while uploading file to Sage.'
        console.error('Error uploading file to Sage:', {
          errorMessage,
          requestPayload: { file, organization, metadata },
        })
        throw new Error(errorMessage)
      }
    }

    /**
     * Fetches responses using V2 LLM service with Pinecone RAG.
     * @param requestPayload - The payload containing the query and context.
     * @returns A promise that resolves with the LLM query response.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const fetchV2LLMQuery = async (
      requestPayload: FetchV2LLMRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL for V2 API
      const url = `${LLM_SERVICE}/api/v2/gemini/generate`
      try {
        const response: AxiosResponse<FetchSageQueryResponse> =
          await axios.post(url, {
            content: requestPayload.content,
            rolodex_files: requestPayload.rolodex_files || [],
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const responseData = axiosError.response?.data as
          | SageErrorResponsePayload
          | undefined
        const errorMessage =
          responseData?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching V2 LLM query response.'
        const status = axiosError.response?.status
        const errorDetails = responseData?.details

        console.error('Error fetching V2 LLM query response:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    /**
     * Fetches responses using RAG Pinecone endpoint with organization and unit filtering.
     * @param requestPayload - The payload containing the query, filters, and context.
     * @returns A promise that resolves with the LLM query response.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const fetchAIAQuery = async (
      requestPayload: FetchRAGPineconeRequest
    ): Promise<FetchSageQueryResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      const url = `${LLM_SERVICE}/api/v2/aia/generate`
      try {
        const response: AxiosResponse<FetchSageQueryResponse> =
          await axios.post(url, {
            content: requestPayload.content,
            filters: requestPayload.filters,
            additional_context: requestPayload.additional_context,
            organization_id: requestPayload.organization_id,
            top_k: requestPayload.top_k,
            rolodex_files: requestPayload.rolodex_files || [],
            response_schema: requestPayload.response_schema,
            include_rug_data:
              requestPayload.include_rug_data === undefined
                ? true
                : requestPayload.include_rug_data,
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const responseData = axiosError.response?.data as
          | SageErrorResponsePayload
          | undefined
        const errorMessage =
          responseData?.error ||
          axiosError.message ||
          'An unknown error occurred while fetching RAG Pinecone query response.'
        const status = axiosError.response?.status
        const errorDetails = responseData?.detail

        console.error('Error fetching RAG Pinecone query response:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    /**
     * Fast uploads a file to Pinecone for RAG purposes using algorithmic chunking.
     * @param requestPayload - The payload containing the rolodex file, organization ID, and optional metadata.
     * @returns A promise that resolves with the upload response.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const fastUploadToPinecone = async (
      requestPayload: FastUploadToPineconeRequest
    ): Promise<FastUploadToPineconeResponse> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL for fast upload API
      const url = `${LLM_SERVICE}/api/v2/rag/pinecone/fast-upload`

      try {
        const response: AxiosResponse<FastUploadToPineconeResponse> =
          await axios.post(url, requestPayload, {
            headers: {
              'Content-Type': 'application/json',
            },
          })
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const responseData = axiosError.response?.data as
          | SageErrorResponsePayload
          | undefined
        const errorMessage =
          responseData?.error ||
          axiosError.message ||
          'An unknown error occurred while uploading file to Pinecone.'
        const status = axiosError.response?.status
        const errorDetails = responseData?.details

        console.error('Error uploading file to Pinecone:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload: {
            organization_id: requestPayload.organization_id,
            rolodex_file: requestPayload.rolodex_file,
            tags: requestPayload.tags,
            add_metadata: true,
          },
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    /**
     * Checks if a chunk with the specified tag exists in the Pinecone database.
     * @param requestPayload - The payload containing tag name, tag value, and organization ID.
     * @returns A promise that resolves with the check response indicating if the file exists.
     * @throws {SageApiError} If the API call fails or returns an error.
     * @throws {Error} If the LLM service URL is not configured.
     */
    const checkFileId = async (
      requestPayload: CheckFileIdRequest
    ): Promise<boolean> => {
      if (!LLM_SERVICE) {
        console.error(
          'LLM Service URL is not configured. Please set LLM_SERVICE in runtime variables.'
        )
        throw new Error('LLM Service URL is not configured.')
      }

      // Construct URL with query parameters for check file ID API
      const url = new URL(`${LLM_SERVICE}/api/v2/rag/pinecone/find-file`)
      url.searchParams.append('tag_name', requestPayload.tag_name)
      url.searchParams.append('tag_value', requestPayload.tag_value)
      url.searchParams.append('organization_id', requestPayload.organization_id)

      try {
        const response: AxiosResponse<boolean> = await axios.get(
          url.toString(),
          {
            headers: {
              Accept: 'application/json',
            },
          }
        )
        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        const responseData = axiosError.response?.data as
          | SageErrorResponsePayload
          | undefined
        const errorMessage =
          responseData?.error ||
          axiosError.message ||
          'An unknown error occurred while checking file existence in Pinecone.'
        const status = axiosError.response?.status
        const errorDetails = responseData?.details

        console.error('Error checking file existence in Pinecone:', {
          errorMessage,
          status,
          errorDetails,
          requestPayload,
        })

        throw {
          message: errorMessage,
          status,
          details: errorDetails,
        } as SageApiError
      }
    }

    return {
      fetchVertexQuery,
      fetchAIAQuery,
      uploadFileToSage,
      fetchV2LLMQuery,
      fastUploadToPinecone,
      checkFileId,
    }
  }, [LLM_SERVICE, axios])
}
